/**
 * PermitRequirementsChecklist — operator-facing per-job requirement list.
 * Reversible 3-state (pending → complete → n/a). Mutations are pure;
 * parent owns `setJobs`.
 */
import { useMemo, useState, useEffect, useRef } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import { CheckCircle2, Circle, ExternalLink, FileText, Link2, MinusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  REQUIREMENT_CATEGORY_LABEL,
  type RequirementItem,
  type RequirementCategory,
} from "@/data/permitting/requirements";
import {
  getRequirementStatus,
  getRequirementLinkUrl,
  type RequirementStatus,
} from "@/data/permitting/requirementsChecklist";
import { FileUploader } from "@/components/ui/file-uploader";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { useRole } from "@/data/RoleContext";
import type { Attachment } from "@/data/attachmentTypes";
import { getDownloadUrl, getAttachments } from "@/services/attachment.service";
import type { Job } from "@/data/types";

const CATEGORIES_WITH_UPLOAD = new Set<RequirementCategory>(['drawings', 'documents', 'fees']);

const ATTACHMENT_TYPE_TO_CATEGORY: Record<string, RequirementCategory> = {
  permit: 'drawings',
  document: 'documents',
  invoice: 'fees',
};

const UPLOAD_CONFIG: Record<string, {
  attachmentType: 'permit' | 'document' | 'invoice';
  acceptedTypes: string[];
  maxFiles: number;
  maxSizeMB: number;
}> = {
  drawings: {
    attachmentType: 'permit',
    acceptedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
    maxFiles: 5,
    maxSizeMB: 20,
  },
  documents: {
    attachmentType: 'document',
    acceptedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    maxFiles: 10,
    maxSizeMB: 20,
  },
  fees: {
    attachmentType: 'invoice',
    acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    maxFiles: 5,
    maxSizeMB: 10,
  },
};

interface UploadedFile {
  name: string;
  mimeType: string;
  previewUrl: string; // public Supabase URL — used as <img src> for images, href for docs
}

function resolveOperator(repName?: string, roleLabel?: string): string {
  if (repName?.trim()) return repName.trim();
  if (roleLabel?.trim()) return roleLabel.trim();
  return "Unknown Operator";
}

interface Props {
  job: Job;
  items: RequirementItem[];
  onSetStatus: (jobId: string, key: string, status: RequirementStatus, updatedBy: string) => void;
  onSetLinkUrl?: (jobId: string, key: string, url: string) => void;
}

export function PermitRequirementsChecklist({ job, items, onSetStatus, onSetLinkUrl }: Props) {
  const { repName } = useCurrentRep();
  const { roleInfo } = useRole();
  const operator = useMemo(() => resolveOperator(repName, roleInfo?.label), [repName, roleInfo?.label]);
  const [uploaded, setUploaded] = useState<Record<string, UploadedFile[]>>({});
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);

  // Load attachments already saved to Supabase so they persist across modal open/close.
  useEffect(() => {
    let cancelled = false;
    getAttachments("job", job.id).then(async (atts) => {
      if (cancelled) return;
      const relevant = atts.filter(
        (a) => a.attachmentType === "permit" || a.attachmentType === "document" || a.attachmentType === "invoice",
      );
      const grouped: Record<string, UploadedFile[]> = {};
      await Promise.all(
        relevant.map(async (a) => {
          const cat = ATTACHMENT_TYPE_TO_CATEGORY[a.attachmentType];
          if (!cat) return;
          let previewUrl = a.filePath;
          try { previewUrl = await getDownloadUrl(a.id); } catch { /* keep filePath */ }
          if (cancelled) return;
          grouped[cat] = [...(grouped[cat] ?? []), { name: a.fileName, mimeType: a.mimeType ?? "", previewUrl }];
        }),
      );
      if (!cancelled) setUploaded(grouped);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [job.id]);

  async function handleUploadComplete(cat: RequirementCategory, atts: Attachment[]) {
    const files = await Promise.all(
      atts.map(async (a) => {
        let previewUrl = a.filePath; // fallback
        try {
          previewUrl = await getDownloadUrl(a.id);
        } catch {
          // keep filePath as fallback
        }
        return {
          name: a.fileName,
          mimeType: a.mimeType ?? '',
          previewUrl,
        };
      })
    );
    setUploaded((prev) => ({
      ...prev,
      [cat]: [...(prev[cat] ?? []), ...files],
    }));
  }

  /** Tracks in-flight link URL edits before blur-save. */
  const [linkDrafts, setLinkDrafts] = useState<Record<string, string>>({});

  /** Sync link drafts when job changes (e.g. after a save). */
  useEffect(() => {
    // Reset only if job id changes — avoids clobbering user typing on re-renders.
  }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleLinkBlur(key: string) {
    const url = linkDrafts[key] ?? getRequirementLinkUrl(job, key);
    onSetLinkUrl?.(job.id, key, url);
  }

  const grouped = useMemo(() => {
    const map = new Map<RequirementCategory, RequirementItem[]>();
    for (const item of items) {
      const arr = map.get(item.category) ?? [];
      arr.push(item);
      map.set(item.category, arr);
    }
    return Array.from(map.entries());
  }, [items]);

  if (items.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        No requirements applicable for this permit context.
      </p>
    );
  }

  const cycle = (current: RequirementStatus): RequirementStatus =>
    current === "pending" ? "complete" : current === "complete" ? "n_a" : "pending";

  return (
    <div className="space-y-4">
      {grouped.map(([cat, catItems]) => (
        <div key={cat}>
          <h5 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
            {REQUIREMENT_CATEGORY_LABEL[cat]}
          </h5>
          <ul className="space-y-1">
            {catItems.map((item) => {
              const status = getRequirementStatus(job, item.key);
              const isLink = item.type === "link";
              const Icon =
                status === "complete" ? CheckCircle2 :
                status === "n_a" ? MinusCircle : Circle;
              const tone =
                status === "complete" ? "text-success" :
                status === "n_a" ? "text-muted-foreground" : "text-muted-foreground/60";
              const savedUrl = getRequirementLinkUrl(job, item.key);
              const draftUrl = linkDrafts[item.key] ?? savedUrl;
              return (
                <li key={item.key} className="rounded-md border bg-background p-2 space-y-1.5">
                  <div className="flex items-start gap-2">
                    <PermissionGate permission="jobs_approve">
                      <button
                        type="button"
                        onClick={() => onSetStatus(job.id, item.key, cycle(status), operator)}
                        className={`mt-0.5 transition-colors hover:opacity-80 ${tone}`}
                        title="Click to cycle: pending → complete → N/A → pending"
                        aria-label={`Toggle requirement ${item.label}`}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    </PermissionGate>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isLink && <Link2 className="h-3 w-3 text-muted-foreground shrink-0" />}
                        <span className={`text-sm font-medium ${
                          status === "complete" ? "text-foreground line-through opacity-70" :
                          status === "n_a" ? "text-muted-foreground line-through opacity-70" :
                          "text-foreground"
                        }`}>
                          {item.label}
                        </span>
                        {status === "n_a" && (
                          <Badge variant="outline" className="text-[10px]">N/A</Badge>
                        )}
                      </div>
                      {item.hint && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">{item.hint}</p>
                      )}
                    </div>
                    {status !== "pending" && (
                      <PermissionGate permission="jobs_approve">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[10px] text-muted-foreground"
                          onClick={() => onSetStatus(job.id, item.key, "pending", operator)}
                        >
                          Reset
                        </Button>
                      </PermissionGate>
                    )}
                  </div>
                  {isLink && (
                    <div className="flex items-center gap-1.5 pl-6">
                      <Input
                        value={draftUrl}
                        onChange={(e) =>
                          setLinkDrafts((prev) => ({ ...prev, [item.key]: e.target.value }))
                        }
                        onBlur={() => handleLinkBlur(item.key)}
                        placeholder="https://..."
                        className="h-7 text-xs flex-1"
                      />
                      {draftUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          onClick={() => window.open(draftUrl, "_blank", "noopener,noreferrer")}
                          type="button"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {CATEGORIES_WITH_UPLOAD.has(cat) && (() => {
            const cfg = UPLOAD_CONFIG[cat];
            return (
              <div className="mt-2 pt-2 border-t border-border/40">
                <p className="text-[10px] text-muted-foreground mb-1.5">Attach files</p>
                <FileUploader
                  parentType="job"
                  parentId={job.id}
                  attachmentType={cfg.attachmentType}
                  acceptedTypes={cfg.acceptedTypes}
                  maxFiles={cfg.maxFiles}
                  maxSizeMB={cfg.maxSizeMB}
                  onUploadComplete={(atts) => handleUploadComplete(cat, atts)}
                  className="[&_.border-dashed]:p-3"
                />
                {(uploaded[cat] ?? []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(uploaded[cat] ?? []).map((f, i) => {
                      const isImage = f.mimeType.startsWith('image/');
                      return isImage ? (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPreviewFile(f)}
                          className="block h-14 w-14 rounded-md overflow-hidden border border-border hover:ring-2 ring-primary/40 transition-all shrink-0"
                        >
                          <img src={f.previewUrl} alt={f.name} className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPreviewFile(f)}
                          className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-muted/40 text-[11px] text-foreground hover:bg-muted transition-colors max-w-[180px] shrink-0"
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate">{f.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      ))}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && setPreviewFile(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-sm font-medium truncate">{previewFile?.name}</DialogTitle>
          </DialogHeader>
          <div className="p-4 flex items-center justify-center min-h-[40vh]">
            {previewFile?.mimeType.startsWith('image/') ? (
              <img src={previewFile.previewUrl} alt={previewFile.name} className="max-w-full max-h-[65vh] object-contain" />
            ) : (
              <iframe src={previewFile?.previewUrl} className="w-full h-[65vh]" title={previewFile?.name} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
